import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, Subscription, catchError, distinctUntilChanged, filter, forkJoin, map, of } from 'rxjs';
import { FilterStateService } from '@services/filter-state/filter-state.service';
import { StructureLabelsService } from '@services/structure/structure-labels.service';
import { StructureService } from '@services/structure/structure.service';
import { ProjectStructureEntity } from '@models/project-structure-entities.model';
import { normalizeLanguageTag } from '@app/core/utils/language';
import { ProjectContextService } from '@services/project-context/project-context.service';
import { isSystemContentType } from '@app/core/constants/system-content-types.constant';

type BreadcrumbKind = 'year' | 'month' | 'day' | 'view' | 'hierarchy';

interface BreadcrumbItem {
  label: string;
  url: string;
  kind: BreadcrumbKind;
}

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss'
})
export class BreadcrumbComponent implements OnInit, OnChanges, OnDestroy {
  breadcrumbs: BreadcrumbItem[] = [];
  
  @Input() view: string = '';
  @Input() path: string = '';

  private readonly subscriptions = new Subscription();
  private readonly entityNameCache = new Map<string, Map<string, string>>();

  constructor(
    private readonly translocoSrv: TranslocoService,
    private readonly router: Router,
    private readonly filterStateService: FilterStateService,
    private readonly structureLabelsService: StructureLabelsService,
    private readonly structureService: StructureService,
    private readonly projectContext: ProjectContextService
  ) {}

  ngOnInit(): void {
    // Language changes affect both local labels and server-side translated entity names.
    this.subscriptions.add(
      this.translocoSrv.langChanges$.subscribe(() => {
        // Ensure we don't keep unbounded caches across languages.
        this.entityNameCache.clear();
        this.rebuildBreadcrumbs();
      })
    );
    // Ensure breadcrumbs track URL changes even if parent inputs don't update.
    this.subscriptions.add(
      this.router.events.pipe(filter((evt) => evt instanceof NavigationEnd)).subscribe(() => this.rebuildBreadcrumbs())
    );
    // Rebuild when year/month/day selection changes.
    this.subscriptions.add(
      this.filterStateService.state$.pipe(
        map(s => [s.selectedYear, s.selectedMonth, s.selectedDay]),
        distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2])
      ).subscribe(() => this.rebuildBreadcrumbs())
    );
    this.rebuildBreadcrumbs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['view'] || changes['path']) {
      this.rebuildBreadcrumbs();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private rebuildBreadcrumbs(): void {
    const snapshot = this.filterStateService.getSnapshot();
    const year = snapshot.selectedYear ?? 0;
    const month = snapshot.selectedMonth ?? 0;

    const next: BreadcrumbItem[] = [];
    next.push({
      label: year > 0 ? year.toString() : '',
      url: '/home',
      kind: 'year'
    });
    next.push({
      label: this.getMonthByLang(month),
      url: '/home',
      kind: 'month'
    });

    const day = snapshot.selectedDay;
    if (day !== null && day > 0) {
      next.push({
        label: day.toString(),
        url: '/home',
        kind: 'day'
      });
    }

    const path = (this.router.url || this.path || '').split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);
    const lang = normalizeLanguageTag(this.translocoSrv.getActiveLang(), 'es');

    // Reserved routes keep the legacy "single view" breadcrumb.
    const reserved = new Set(['home', 'login', 'charts', 'users', 'devices', 'legal-disclaimer', 'cookie-policy']);
    if (segments.length > 0 && reserved.has(segments[0])) {
      const normalizedView = (this.view || '').trim();
      if (normalizedView.length > 0) {
        const structureLabel = this.structureLabelsService.getLabel(normalizedView, lang);
        const label =
          structureLabel !== normalizedView ? structureLabel : this.translocoSrv.translate(`${normalizedView}.title`);
        next.push({ label, url: normalizedView, kind: 'view' });
      }
      this.breadcrumbs = next;
      return;
    }

    const nodes = this.parseNodes(segments);
    if (!nodes) {
      // Fallback: show at least the leaf type + "Todos" so hierarchy navigation is reflected.
      const fallbackType = (segments[segments.length - 1] || (this.view || '')).trim();
      if (fallbackType.length > 0) {
        next.push(...this.buildHierarchyCrumbs([{ contentTypeId: fallbackType, entityId: 'ALL' }], lang));
        this.breadcrumbs = next;
        return;
      }

      this.breadcrumbs = next;
      return;
    }

    const neededTypes = new Set<string>();
    for (const node of nodes) {
      if (node.entityId !== 'ALL') {
        neededTypes.add(node.contentTypeId);
      }
    }

    // Build immediately with cached names (or fallback ids) to avoid a blank breadcrumb.
    next.push(...this.buildHierarchyCrumbs(nodes, lang));
    this.breadcrumbs = next;

    const missing = [...neededTypes]
      .filter((type) => !isSystemContentType(type))
      .filter((type) => !this.entityNameCache.has(this.entityCacheKey(lang, type)));
    if (missing.length === 0) {
      return;
    }

    const projectId = this.filterStateService.getSnapshot().projectId || this.projectContext.getSelectedProjectIdSnapshot();
    if (!projectId) {
      return;
    }
    const requests: Record<string, Observable<ProjectStructureEntity[]>> = {};
    for (const type of missing) {
      requests[type] = this.structureService.getProjectStructureEntities(projectId, type, lang).pipe(
        map((response) => response.entities || []),
        catchError(() => of([]))
      );
    }

    this.subscriptions.add(
      forkJoin(requests).subscribe((results) => {
        for (const [type, entities] of Object.entries(results)) {
          const byId = new Map<string, string>();
          for (const entity of entities) {
            byId.set(entity.id, entity.name);
          }
          this.entityNameCache.set(this.entityCacheKey(lang, type), byId);
        }

        // Rebuild again, now with entity names.
        this.breadcrumbs = [...next.slice(0, 2), ...this.buildHierarchyCrumbs(nodes, lang)];
      })
    );
  }

  private entityCacheKey(lang: string, contentTypeId: string): string {
    return `${normalizeLanguageTag(lang, 'es')}|${contentTypeId.trim()}`;
  }

  private buildHierarchyCrumbs(nodes: Array<{ contentTypeId: string; entityId: string }>, lang: string): BreadcrumbItem[] {
    const crumbs: BreadcrumbItem[] = [];
    const allLabel = this.translocoSrv.translate('common.all');

    const urlForAllAtLevel = (levelIndex: number): string => {
      if (levelIndex <= 0) {
        return `/${nodes[0].contentTypeId}`;
      }

      const parts: string[] = [];
      for (let i = 0; i < levelIndex; i += 1) {
        parts.push(nodes[i].contentTypeId, nodes[i].entityId);
      }
      parts.push(nodes[levelIndex].contentTypeId);
      return `/${parts.join('/')}`;
    };

    const urlForEntityAtLevel = (levelIndex: number): string => {
      const parts: string[] = [];
      for (let i = 0; i <= levelIndex; i += 1) {
        parts.push(nodes[i].contentTypeId, nodes[i].entityId);
      }
      return `/${parts.join('/')}`;
    };

    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      const typeLabel = this.structureLabelsService.getLabel(node.contentTypeId, lang);
      crumbs.push({
        label: typeLabel,
        url: urlForAllAtLevel(i),
        kind: 'hierarchy'
      });

      const entityLabel =
        node.entityId === 'ALL'
          ? allLabel
          : this.entityNameCache.get(this.entityCacheKey(lang, node.contentTypeId))?.get(node.entityId) || node.entityId;

      crumbs.push({
        label: entityLabel,
        url: node.entityId === 'ALL' ? urlForAllAtLevel(i) : urlForEntityAtLevel(i),
        kind: 'hierarchy'
      });
    }

    return crumbs;
  }

  private parseNodes(segments: string[]): Array<{ contentTypeId: string; entityId: string }> | null {
    if (!segments || segments.length === 0) {
      return null;
    }

    if (segments.length === 1) {
      return [{ contentTypeId: segments[0], entityId: 'ALL' }];
    }

    if (segments.length % 2 === 0) {
      const nodes: Array<{ contentTypeId: string; entityId: string }> = [];
      for (let i = 0; i < segments.length; i += 2) {
        const contentTypeId = segments[i];
        const entityId = segments[i + 1];
        if (!contentTypeId || !entityId) {
          return null;
        }
        nodes.push({ contentTypeId, entityId });
      }
      return nodes;
    }

    // Trailing typeId means "ALL" at that level, scoped by its parent.
    if (segments.length >= 3) {
      const nodes: Array<{ contentTypeId: string; entityId: string }> = [];
      for (let i = 0; i < segments.length - 1; i += 2) {
        const contentTypeId = segments[i];
        const entityId = segments[i + 1];
        if (!contentTypeId || !entityId) {
          return null;
        }
        nodes.push({ contentTypeId, entityId });
      }
      const trailingTypeId = segments[segments.length - 1];
      if (!trailingTypeId) {
        return null;
      }
      nodes.push({ contentTypeId: trailingTypeId, entityId: 'ALL' });
      return nodes;
    }

    return null;
  }

  getMonthByLang(month: number): string {
      if (month <= 0) {
        return '';
      }

      return this.translocoSrv.translate(`date.${month - 1}`);
  }

  onBreadcrumbClick(item: BreadcrumbItem, index: number): void {
    if (item.kind === 'view') {
      return;
    }

    const clickSnapshot = this.filterStateService.getSnapshot();
    const year = clickSnapshot.selectedYear ?? 0;
    const month = clickSnapshot.selectedMonth ?? 0;
    if (year <= 0) {
      this.router.navigate(['/home']);
      return;
    }

    if (index === 0) {
      this.filterStateService.selectYear(year);
      this.router.navigate(['/home']);
      return;
    }

    if (index === 1) {
      this.filterStateService.selectYear(year);
      if (month > 0) {
        this.filterStateService.selectMonth(month);
      }
      this.router.navigate(['/home']);
      return;
    }

    if (item.kind === 'day') {
      this.router.navigate(['/home']);
      return;
    }

    if (item.kind === 'hierarchy' && item.url) {
      this.router.navigate([item.url]);
    }
  }
}
